import { appGenerateRequestSchema, appGenerateResultSchema, generationLimits, } from './app-generation.js';
import { AIError, errorCodes, } from './errors.js';
import { parseOrigin, readErrorResponse } from './response-errors.js';
import { parseRequest, parseResponse } from './validation.js';
export * from './app-generation.js';
export { AIError, errorCodes } from './errors.js';
function isOutputSchema(schema) {
    const possibleSchema = schema;
    return typeof possibleSchema?.safeParse === 'function'
        && typeof possibleSchema.toJSONSchema === 'function';
}
/**
 * Framework-free client for product frontends. Holds no credentials and never
 * retries; the AI API picks the provider unless the request names one.
 */
export function createAIClient(options) {
    if (typeof options?.transport !== 'function') {
        throw new Error('createAIClient requires a transport.');
    }
    return {
        async generate(requestInput) {
            const suppliedSchema = requestInput?.format?.schema;
            const outputSchema = isOutputSchema(suppliedSchema)
                ? suppliedSchema
                : undefined;
            // Convert runtime schemas for the backend; leave plain JSON Schema objects intact.
            let requestToValidate = requestInput;
            if (outputSchema) {
                // `$schema` is metadata providers reject; drop it from the converted schema.
                const { $schema: _metadata, ...jsonSchema } = outputSchema.toJSONSchema();
                requestToValidate = {
                    ...requestInput,
                    format: {
                        ...requestInput.format,
                        schema: jsonSchema,
                    },
                };
            }
            const request = parseRequest(appGenerateRequestSchema, requestToValidate);
            const requestSizeBytes = new TextEncoder().encode(JSON.stringify(request)).byteLength;
            if (requestSizeBytes > generationLimits.maxRequestBytes) {
                throw new AIError('The AI request is too large.', 'invalid_request', 413);
            }
            let responseBody;
            try {
                responseBody = await options.transport(request);
            }
            catch (error) {
                // Transports should wrap expected backend failures in AIError.
                if (error instanceof AIError) {
                    throw error;
                }
                throw new AIError('Unable to reach the AI service.', 'network_error', 0);
            }
            const result = parseResponse(appGenerateResultSchema, responseBody);
            if (request.format && result.data === undefined) {
                throw new AIError('The AI service omitted structured output.', 'invalid_response', 502, result.requestId);
            }
            if (outputSchema) {
                const validatedOutput = outputSchema.safeParse(result.data);
                if (!validatedOutput.success) {
                    throw new AIError('AI returned an unexpected result.', 'schema_failure', 422, result.requestId);
                }
                return {
                    ...result,
                    data: validatedOutput.data,
                };
            }
            return result;
        },
    };
}
/**
 * Sends requests straight to the AI API's app route with a product-issued
 * token. Retries once, and only after a 401: a rejected token means nothing ran.
 */
export function tokenTransport(options) {
    if (typeof options?.getToken !== 'function') {
        throw new Error('tokenTransport requires getToken.');
    }
    const timeoutMs = options.timeoutMs ?? 70_000;
    if (!Number.isSafeInteger(timeoutMs) || timeoutMs < 1) {
        throw new Error('Invalid timeoutMs.');
    }
    async function send(request, token) {
        // Look fetch up per request so importing this module touches no browser globals.
        const sendRequest = options.fetch ?? globalThis.fetch;
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        try {
            return await sendRequest(new URL('/v1/app/generate', parseOrigin(token.baseUrl)), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token.token}`,
                },
                body: JSON.stringify(request),
                signal: controller.signal,
                // The token is the only credential; never send cookies or follow a redirect with it.
                credentials: 'omit',
                redirect: 'error',
            });
        }
        catch (error) {
            if (error instanceof Error && ['TimeoutError', 'AbortError'].includes(error.name)) {
                throw new AIError('The AI request timed out.', 'timeout', 0);
            }
            throw new AIError('Unable to reach the AI service.', 'network_error', 0);
        }
        finally {
            clearTimeout(timer);
        }
    }
    return async (request) => {
        let response = await send(request, await options.getToken(false));
        if (response.status === 401) {
            await response.body?.cancel();
            response = await send(request, await options.getToken(true));
        }
        let body;
        try {
            body = await response.json();
        }
        catch {
            if (response.ok) {
                throw new AIError('The AI service returned an invalid response.', 'invalid_response', 502);
            }
        }
        if (!response.ok) {
            throw readErrorResponse(response, body);
        }
        return body;
    };
}
const jwtPattern = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;
// Tokens are refreshed this long before they expire, so a request never starts with one about to lapse.
const refreshBeforeExpirySeconds = 60;
/** Maps the PHP `{status: 'error', error}` envelope; dispatcher failures stay generic. */
function ajaxError(response) {
    const backendError = response?.error;
    let errorCode = 'upstream_error';
    if (typeof backendError?.code === 'string' && errorCodes.includes(backendError.code)) {
        errorCode = backendError.code;
    }
    // Dispatcher failures (status "Error: …") carry server details; never surface them.
    let errorMessage = 'AI could not complete this request.';
    if (typeof backendError?.message === 'string') {
        errorMessage = backendError.message;
    }
    let httpStatus = 502;
    if (typeof backendError?.status === 'number' && Number.isInteger(backendError.status)) {
        httpStatus = backendError.status;
    }
    const requestId = typeof response?.requestId === 'string'
        ? response.requestId
        : undefined;
    return new AIError(errorMessage, errorCode, httpStatus, requestId);
}
function readToken(response) {
    if (response?.status !== 'ok') {
        throw ajaxError(response);
    }
    const isValid = typeof response.token === 'string' && jwtPattern.test(response.token)
        && typeof response.expires_at === 'number' && Number.isFinite(response.expires_at)
        && typeof response.base_url === 'string';
    if (isValid) {
        try {
            parseOrigin(response.base_url);
            return {
                token: response.token,
                expiresAt: response.expires_at,
                baseUrl: response.base_url,
            };
        }
        catch {
            // Reported below with the other malformed responses.
        }
    }
    throw new AIError('The AI token service returned an invalid response.', 'invalid_response', 502);
}
/**
 * Gets tokens from the product backend through `U.ajax_promise(service, params)`.
 * Keeps one token for the current user, reuses it until shortly before it
 * expires, and shares a single request between concurrent callers.
 */
export function ajaxTokenSource(ajax, options = {}) {
    const serviceName = options.service ?? 'get_ai_token';
    let cached;
    let pending;
    return async (force = false) => {
        // U.ajax adds fields to the object it is given, so build a fresh one per call.
        const params = { ...options.params?.() };
        const user = String(params.user_id ?? '');
        const nowSeconds = Date.now() / 1000;
        if (!force && cached?.user === user && cached.token.expiresAt - refreshBeforeExpirySeconds > nowSeconds) {
            return cached.token;
        }
        if (pending?.user === user) {
            return pending.request;
        }
        const request = ajax(serviceName, params)
            .then(response => {
            const token = readToken(response);
            cached = { user, token };
            return token;
        })
            .finally(() => {
            if (pending?.request === request) {
                pending = undefined;
            }
        });
        pending = { user, request };
        return request;
    };
}
